import { Badge, BadgeVariant } from '@/components/ui/badge';
import { cn } from "@/lib/utils";

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'cancelled'
  | 'paused'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid';

export interface SubscriptionStatusBadgeProps {
  status: SubscriptionStatus | string;
  className?: string;
}

// Get badge variant based on subscription status
const getSubscriptionStatusBadgeVariant = (status: string): BadgeVariant => {
  const normalizedStatus = status?.toLowerCase() || '';

  // Success states
  if (normalizedStatus === 'active') return 'green';

  // Trial/pending states
  if (['trialing', 'incomplete'].includes(normalizedStatus)) return 'blue';

  // Warning states
  if (['past_due', 'unpaid'].includes(normalizedStatus)) return 'yellow';

  // Paused/inactive states
  if (normalizedStatus === 'paused') return 'gray';

  // Error/failure states
  if (['canceled', 'cancelled', 'incomplete_expired'].includes(normalizedStatus)) return 'red';

  return 'gray';
};

// Get display label for subscription status
const getSubscriptionStatusLabel = (status: string): string => {
  const normalizedStatus = status?.toLowerCase() || '';

  const statusMap: Record<string, string> = {
    active: 'Active',
    trialing: 'Trialing',
    past_due: 'Past Due',
    canceled: 'Canceled',
    cancelled: 'Canceled',
    paused: 'Paused',
    incomplete: 'Incomplete',
    incomplete_expired: 'Incomplete Expired',
    unpaid: 'Unpaid',
  };

  return (
    statusMap[normalizedStatus] ||
    status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) ||
    'Unknown'
  );
};

export function SubscriptionStatusBadge({ status, className }: SubscriptionStatusBadgeProps) {
  return (
    <Badge variant={getSubscriptionStatusBadgeVariant(status)} className={cn('min-w-[72px] justify-center', className)}>
      {getSubscriptionStatusLabel(status)}
    </Badge>
  );
}
